import { computed, type ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { SUPPORTED_LOCALES, type Locale } from '@/i18n/locales';

const STORAGE_KEY = 'bafsy.locale';

/**
 * Проверяет, что строка — поддерживаемая локаль.
 */
export function isSupportedLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

export interface UseLocaleReturn {
  readonly locale: ComputedRef<Locale>;
  readonly setLocale: (next: string) => Promise<void>;
  readonly supportedLocales: typeof SUPPORTED_LOCALES;
  readonly availableLocales: readonly string[];
  readonly t: (key: string, ...args: readonly unknown[]) => string;
}

/**
 * Обёртка над `useI18n`, плюс синхронизация с `<html lang>`,
 * `localStorage` и vue-router.
 */
export function useLocale(): UseLocaleReturn {
  const { locale, t, availableLocales } = useI18n();
  const router = useRouter();
  const route = useRoute();

  /**
   * Установить локаль. Бросает, если локаль не поддерживается.
   * Обновляет i18n, localStorage, `<html lang>` и текущий url.
   */
  async function setLocale(next: string): Promise<void> {
    if (!isSupportedLocale(next)) {
      throw new Error(`[useLocale] unsupported locale: ${next}`);
    }
    locale.value = next;
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage может быть заблокирован, это не критично.
      }
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = next;
    }

    // Переезжаем на ту же страницу, но с новым lang-префиксом,
    // сохраняя path, hash и query.
    const currentLang = route.params.lang as string | undefined;
    if (currentLang && currentLang !== next) {
      const newPath = route.fullPath.replace(new RegExp(`^/${currentLang}(/|$)`), `/${next}$1`);
      await router.push(newPath);
    }
  }

  return {
    locale: computed(() => locale.value as Locale),
    setLocale,
    supportedLocales: SUPPORTED_LOCALES,
    availableLocales,
    t: t as UseLocaleReturn['t'],
  };
}
